"use client";

import { CrossIcon } from "@cmp-src/icons/TacticIcons/CrossIcon";
import { cn } from "@gettactic/helpers";
import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { createPortal } from "react-dom";

// Sheet Context
interface SheetContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SheetContext = React.createContext<SheetContextValue | null>(null);

const useSheetContext = () => {
  const context = React.useContext(SheetContext);
  if (!context) {
    throw new Error("Sheet components must be used within a Sheet");
  }
  return context;
};

// Sheet Root
interface SheetProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
}

const Sheet: React.FC<SheetProps> = ({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  defaultOpen = false
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const onOpenChange = React.useCallback(
    (newOpen: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(newOpen);
      }
      controlledOnOpenChange?.(newOpen);
    },
    [isControlled, controlledOnOpenChange]
  );

  const contextValue = React.useMemo(
    () => ({ open, onOpenChange }),
    [open, onOpenChange]
  );

  return (
    <SheetContext.Provider value={contextValue}>
      {children}
    </SheetContext.Provider>
  );
};

// Sheet Trigger
interface SheetTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const SheetTrigger = React.forwardRef<HTMLButtonElement, SheetTriggerProps>(
  ({ children, asChild, onClick, ...props }, ref) => {
    const { onOpenChange } = useSheetContext();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      onOpenChange(true);
    };

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(
        children as React.ReactElement<{
          onClick?: (e: React.MouseEvent) => void;
        }>,
        {
          onClick: (e: React.MouseEvent) => {
            (
              children as React.ReactElement<{
                onClick?: (e: React.MouseEvent) => void;
              }>
            ).props.onClick?.(e);
            onOpenChange(true);
          }
        }
      );
    }

    return (
      <button ref={ref} type="button" onClick={handleClick} {...props}>
        {children}
      </button>
    );
  }
);
SheetTrigger.displayName = "SheetTrigger";

// Sheet Close
interface SheetCloseProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

const SheetClose = React.forwardRef<HTMLButtonElement, SheetCloseProps>(
  ({ children, asChild, onClick, className, ...props }, ref) => {
    const { onOpenChange } = useSheetContext();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      onOpenChange(false);
    };

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(
        children as React.ReactElement<{
          onClick?: (e: React.MouseEvent) => void;
        }>,
        {
          onClick: (e: React.MouseEvent) => {
            (
              children as React.ReactElement<{
                onClick?: (e: React.MouseEvent) => void;
              }>
            ).props.onClick?.(e);
            onOpenChange(false);
          }
        }
      );
    }

    return (
      <button
        ref={ref}
        type="button"
        onClick={handleClick}
        className={className}
        {...props}
      >
        {children}
      </button>
    );
  }
);
SheetClose.displayName = "SheetClose";

// Sheet Portal
interface SheetPortalProps {
  children: React.ReactNode;
  container?: HTMLElement;
}

const SheetPortal: React.FC<SheetPortalProps> = ({ children, container }) => {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return createPortal(children, container || document.body);
};
SheetPortal.displayName = "SheetPortal";

// Sheet Overlay
interface SheetOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  isVisible?: boolean;
}

const SheetOverlay = React.forwardRef<HTMLDivElement, SheetOverlayProps>(
  ({ className, isVisible: isVisibleProp, ...props }, ref) => {
    const { onOpenChange } = useSheetContext();

    return (
      <div
        ref={ref}
        className={cn(
          "fixed inset-0 z-drawer bg-black/20 backdrop-blur-md transition-opacity duration-300",
          isVisibleProp ? "opacity-100" : "opacity-0 pointer-events-none",
          className
        )}
        onClick={() => onOpenChange(false)}
        {...props}
      />
    );
  }
);
SheetOverlay.displayName = "SheetOverlay";

// Sheet Content Variants
const sheetVariants = cva(
  "fixed z-drawer bg-background pt-6 shadow-lg transition-transform duration-300 ease-in-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b",
        bottom: "inset-x-0 bottom-0 border-t",
        left: "inset-y-0 left-0 h-full border-r",
        right: "inset-y-0 right-0 h-full border-l"
      },
      size: {
        sm: "w-full sm:max-w-sm",
        md: "w-full sm:max-w-md",
        lg: "w-full sm:max-w-lg",
        xl: "w-full sm:max-w-xl",
        "2xl": "w-full sm:max-w-2xl",
        "3xl": "w-full sm:max-w-3xl",
        half: "w-full sm:w-1/2",
        full: "w-full"
      }
    },
    defaultVariants: {
      side: "right",
      size: "3xl"
    }
  }
);

// Get transform styles based on side and open state
const getTransformStyle = (
  side: "top" | "bottom" | "left" | "right",
  open: boolean
): React.CSSProperties => {
  if (open) {
    return { transform: "translate(0, 0)" };
  }

  switch (side) {
    case "top":
      return { transform: "translateY(-100%)" };
    case "bottom":
      return { transform: "translateY(100%)" };
    case "left":
      return { transform: "translateX(-100%)" };
    case "right":
      return { transform: "translateX(100%)" };
    default:
      return { transform: "translateX(100%)" };
  }
};

/** Lets `SheetTitle` and `SheetDescription` name and describe their sheet. */
interface SheetLabelsContextValue {
  setTitleId: (id: string | undefined) => void;
  setDescriptionId: (id: string | undefined) => void;
}

const SheetLabelsContext = React.createContext<SheetLabelsContextValue | null>(
  null
);

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Keeps Tab and Shift+Tab inside the sheet, as in any modal dialog. */
const trapTabKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
  if (event.key !== "Tab") return;
  const focusables = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  );
  if (focusables.length === 0) {
    event.preventDefault();
    return;
  }
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === event.currentTarget)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

// Sheet Content
interface SheetContentProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof sheetVariants> {}

/**
 * A modal panel sliding in from a side (`side`) over a blurred backdrop, sized by `size`,
 * named by its `SheetTitle` and described by its `SheetDescription`.
 */
const SheetContent = React.forwardRef<HTMLDivElement, SheetContentProps>(
  ({ side = "right", size, className, children, ...props }, ref) => {
    const { open, onOpenChange } = useSheetContext();
    const [shouldRender, setShouldRender] = React.useState(open);
    const [isVisible, setIsVisible] = React.useState(false);
    const [titleId, setTitleId] = React.useState<string>();
    const [descriptionId, setDescriptionId] = React.useState<string>();
    const labels = React.useMemo(() => ({ setTitleId, setDescriptionId }), []);
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const setRefs = (element: HTMLDivElement | null) => {
      contentRef.current = element;
      if (typeof ref === "function") ref(element);
      else if (ref) ref.current = element;
    };

    // Focus moves into the sheet when it opens and back to where it was when it closes
    React.useEffect(() => {
      if (!open) return;
      const previouslyFocused = document.activeElement as HTMLElement | null;
      const focusTimer = requestAnimationFrame(() => {
        if (!contentRef.current?.contains(document.activeElement)) {
          contentRef.current?.focus();
        }
      });
      return () => {
        cancelAnimationFrame(focusTimer);
        if (previouslyFocused?.isConnected) previouslyFocused.focus();
      };
    }, [open, shouldRender]);

    // Handle escape key
    React.useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape" && open) {
          onOpenChange(false);
        }
      };

      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [open, onOpenChange]);

    // Handle body scroll lock
    React.useEffect(() => {
      if (open) {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
          document.body.style.overflow = originalOverflow;
        };
      }
      return;
    }, [open]);

    // Handle mount/unmount and animation timing
    React.useEffect(() => {
      if (open) {
        setShouldRender(true);
        // Small delay to ensure the element is rendered before animating
        const showTimer = requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsVisible(true);
          });
        });
        return () => cancelAnimationFrame(showTimer);
      } else {
        setIsVisible(false);
        const hideTimer = setTimeout(() => {
          setShouldRender(false);
        }, 300); // Match animation duration
        return () => clearTimeout(hideTimer);
      }
    }, [open]);

    if (!shouldRender) return null;

    return (
      <SheetPortal>
        <SheetOverlay isVisible={isVisible} />
        <div
          ref={setRefs}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          tabIndex={-1}
          className={cn(
            sheetVariants({ side, size }),
            "focus:outline-none",
            className
          )}
          style={getTransformStyle(side ?? "right", isVisible)}
          onKeyDown={trapTabKey}
          {...props}
        >
          <SheetClose className="absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <CrossIcon /> <span className="sr-only">Close</span>
          </SheetClose>
          <SheetLabelsContext.Provider value={labels}>
            {children}
          </SheetLabelsContext.Provider>
        </div>
      </SheetPortal>
    );
  }
);
SheetContent.displayName = "SheetContent";

// Sheet Header
const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center text-left text-2xl px-6 pt-1 pb-2",
      className
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

// Sheet Footer
const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

// Sheet Title
const SheetTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, id, ...props }, ref) => {
  const generatedId = React.useId();
  const titleId = id ?? generatedId;
  const labels = React.useContext(SheetLabelsContext);
  React.useLayoutEffect(() => {
    labels?.setTitleId(titleId);
    return () => labels?.setTitleId(undefined);
  }, [labels, titleId]);
  return (
    <h2
      ref={ref}
      id={titleId}
      className={cn("font-semibold text-3xl text-foreground", className)}
      {...props}
    />
  );
});
SheetTitle.displayName = "SheetTitle";

// Sheet Description
const SheetDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, id, ...props }, ref) => {
  const generatedId = React.useId();
  const descriptionId = id ?? generatedId;
  const labels = React.useContext(SheetLabelsContext);
  React.useLayoutEffect(() => {
    labels?.setDescriptionId(descriptionId);
    return () => labels?.setDescriptionId(undefined);
  }, [labels, descriptionId]);
  return (
    <p
      ref={ref}
      id={descriptionId}
      className={cn("text-content text-sm", className)}
      {...props}
    />
  );
});
SheetDescription.displayName = "SheetDescription";

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger
};
